import { Stack, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { loadScript } from '../globalHeader/common/loadScript'
import { loginSocial, upgradeGuestSocial } from '../globalHeader/functions/ams-functions'
import { analyticsManager } from '../globalHeader/services/analytics/AnalyticsManager'
import { reportSharedError } from '../utils/errorReporting'
import { ServerErrors } from '../utils/serverErrors'
import { SocialType } from './SocialLogin'

declare global {
    interface Window {
        google: any
    }
}

interface IProps {
    googleClientId: string
    isGuest: boolean
    onSignUpSocial: (socialResponse: any) => void
    onLoginSuccessful: () => void
}

const GoogleAuth: React.FC<IProps> = (props) => {
    const { onSignUpSocial, onLoginSuccessful, isGuest, googleClientId } = props
    const [socialType, setSocialType] = useState(SocialType.GOOGLE)
    const googleButton = useRef(null)

    useEffect(() => {
        const src = 'https://accounts.google.com/gsi/client'
        let timerId: NodeJS.Timeout | null = null

        loadScript(src)
            .then(() => {
                const attemptInit = () => {
                    if (window.google && window.google.accounts) {
                        window.google.accounts.id.initialize({
                            client_id: googleClientId,
                            callback: handleResponse,
                        })
                        window.google.accounts.id.renderButton(googleButton.current, {
                            theme: 'filled_black',
                            size: 'large',
                            shape: 'circle',
                            type: 'icon',
                        })
                    } else {
                        timerId = setTimeout(attemptInit, 100)
                    }
                }
                attemptInit()
            })
            .catch((error) => reportSharedError('Google SDK script load failed', error, { src }))

        return () => {
            if (timerId) {
                clearTimeout(timerId)
            }
            const scriptTag = document.querySelector(`script[src="${src}"]`)
            if (scriptTag) document.body.removeChild(scriptTag)
        }
    }, [])

    const handleResponse = async (response: any) => {
        let data: any = null
        if (isGuest) {
            data = await upgradeGuestSocial(response.credential, response.clientId, socialType)
        } else {
            data = await loginSocial(response.credential, response.clientId, socialType)
        }

        if (data.error) {
            if (
                data.error.code === ServerErrors.USERNAME_IS_MANDATORY_FOR_FIRST_LOGIN ||
                data.error.code === ServerErrors.SOCIAL_LOGIN_MANDATORY_FIELDS_IS_MISSING
            ) {
                onSignUpSocial({ ...response, socialType })
            } else {
                console.log('Google auth error:', data.error)
            }
        } else {
            analyticsManager.dispatchEvent('completeLogin', {
                authType: 'google',
                userId: data.user.id,
                userName: data.user.user_name,
            })
            onLoginSuccessful()
        }
    }

    return (
        <Stack
            gap={0.5}
            sx={{
                alignItems: 'center',
                colorScheme: 'light',
            }}
        >
            <div style={{ height: '44px', overflow: 'hidden' }} ref={googleButton}></div>
            <Typography variant="body2">Google</Typography>
        </Stack>
    )
}

GoogleAuth.displayName = 'GoogleAuth'

export default GoogleAuth
